import { accountApi } from "@/apis/accounts/accountApi";
import { ApiResponseError } from "@/apis/errors";
import { Button } from "@/components/atoms/buttons/Button";
import { ButtonText } from "@/components/atoms/buttons/ButtonText";
import { PageHeader } from "@/components/organisms/header/PageHeader";
import { LayoutTemplate } from "@/components/templates/layout";
import useApiResponseErrorHandler from "@/hooks/useApiResponseErrorHandler";
import { useUserAccount } from "@/states/auth/selectoers";
import { useLoading } from "@/states/loading/operations";
import type { NextPageWithLayout } from "next";
import { ReactElement, useState } from "react";
import { useGetAccount } from "@/states/auth/operations";
import DeleteModal from "@/components/molecules/modals/DeleteModal";
import ErrorLimitByPlanModal from "@/components/organisms/modals/ErrorLimitByPlanModal";

const AccountSyncZoomCalendarPage: NextPageWithLayout = () => {
  const account = useUserAccount();
  const getAccount = useGetAccount();
  const withLoading = useLoading();
  const { handlePageError } = useApiResponseErrorHandler();

  const [isOpenDeleteZoom, setIsOpenDeleteZoom] = useState<boolean>(false);

  const handleConnectZoomCalendar = () => {
    if (account && account.user_plan_config?.enable_zoom) {
      const url = process.env.NEXT_PUBLIC_ZOOM_AUTH_URL;
      if (url) {
        window.open(url, "_blank", "noopener,noreferrer");
      }
      return;
    }
  };

  const handleDisconnectZoomCalendar = () => {
    if (account?.has_menu_zoom_type) return;

    void withLoading(async () => {
      try {
        setIsOpenDeleteZoom(false);
        await accountApi.postAccountDisconnectZoom();
        await getAccount();
      } catch (e) {
        if (e instanceof ApiResponseError) {
          await handlePageError(e);
          return;
        }
      }
    });
  };

  const [visibleLimitPlanModal, setVisibleLimitPlanModal] = useState<boolean>(!account?.user_plan_config?.enable_zoom);
  const handleClosePopup = () => {
    setVisibleLimitPlanModal(false);
  };
  if (!account?.user_plan_config?.enable_zoom) {
    return (
      <>
        {
          visibleLimitPlanModal && <ErrorLimitByPlanModal onClose={handleClosePopup} reason="ZOOM_SYNC" />
        }
      </>
    );
  }

  return (
    <div className="mb-[60px]">
      {isOpenDeleteZoom && (
        <DeleteModal
          title="zoom連携解除"
          disabledConfirm={account?.has_menu_zoom_type}
          isOpen={isOpenDeleteZoom}
          handleClose={() => setIsOpenDeleteZoom(false)}
          handleSubmit={handleDisconnectZoomCalendar}
          btnText="解除"
        >
          {
            account?.has_menu_zoom_type 
            ? 
              <p>zoom連携しているコンテンツがあるため解除できません。先にコンテンツの「開催・提供方法」のzoom連携を外してください</p>
            :
              <p>zoomとの自動連携を解除すると、「開催・提供方法」で自動連携の選択ができなくなります。zoomとの自動連携を解除しますか？</p>
          }
        </DeleteModal>
      )}
      <article>
        <div className="md:mt-6 mb-10">
          <PageHeader title="Zoom連携"></PageHeader>
        </div>

        <div className="mb-10 space-y-4 rounded-[4px] border border-gray-100">
          <div className="px-4 py-6">
            <h2 className="text-[20px] font-bold">
              zoom連携でできること
            </h2>
          </div>
          <div className="px-4 py-6 border-t border-gray-100">
            <div className="mb-6">
              <p className="text-[#222222] font-bold mb-1">
                zoomのURLを自動発行
              </p>
              <p>予約・申込に合わせて、自動でzoomのURLを発行します。</p>
            </div>
            <div className="flex md:flex-row flex-col gap-4 items-center bg-[#F7F8F9] p-4 ">
              {account && account?.zoom_email ? (
                <>
                  <div>
                    <p className="font-bold">現在連携中のアカウント</p>
                    <p>{account?.zoom_email}</p>
                  </div>
                  <ButtonText
                    label="アカウント連携を解除する"
                    className="ml-auto !w-[193px]"
                    labelClassNameContainer="text-[#D32932] !border-0"
                    onClick={() => setIsOpenDeleteZoom(true)}
                  />
                </>
              ) : (
                <>
                  <p>
                    zoomを設定するには、zoomアカウントを連携してください。
                  </p>
                  <Button
                    label={"zoomアカウントと連携"}
                    className="ml-auto w-full md:!w-[193px] !h-[49px] md:!h-[36px] text-[14px]"
                    onClick={handleConnectZoomCalendar}
                  />
                </>
              )}
            </div>
          </div>
        </div>
      </article>
    </div>
  );
};

AccountSyncZoomCalendarPage.getLayout = function getLayout(
  children: ReactElement,
) {
  return <LayoutTemplate>{children}</LayoutTemplate>;
};

export default AccountSyncZoomCalendarPage;
